import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import {
  API_KEY_PROVIDERS,
  ApiKeysError,
  PROVIDER_PAPEL,
  createApiKey,
  type ApiKeyListItem,
  type ApiKeysErrorReason,
  type ApiKeysProvider,
} from '@/lib/api_keys';

import { mapApiKeysErrorReason } from './error_messages';
import { Field, Input, Select } from '@/components/ui';

// Identificador HTTP do provider — string do contrato, nunca exibida: o rótulo
// vem do i18n (`addDialogProviderNames`). Deixou de ser um valor único na
// E74.F2, quando o bureau cadastral entrou como segunda chave do escritório.
const PROVIDER_PADRAO: ApiKeysProvider = 'anthropic'; // ux-neutrality:allow api-contract; branding:allow

interface ApiKeyAddDialogProps {
  onSuccess(item: ApiKeyListItem): void;
  onCancel(): void;
}

export function ApiKeyAddDialog({ onSuccess, onCancel }: ApiKeyAddDialogProps) {
  const apiClient = useApiClient();
  const [provider, setProvider] = useState<ApiKeysProvider>(PROVIDER_PADRAO);
  const [value, setValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorReason, setErrorReason] = useState<ApiKeysErrorReason | null>(null);

  async function handleSubmit(evt: FormEvent<HTMLFormElement>) {
    evt.preventDefault();
    setSubmitting(true);
    setErrorReason(null);
    try {
      const item = await createApiKey(apiClient, provider, value);
      onSuccess(item);
    } catch (err) {
      const reason: ApiKeysErrorReason = err instanceof ApiKeysError ? err.reason : 'unknown';
      setErrorReason(reason);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && !submitting && onCancel()}>
      <DialogContent
        data-testid="api-key-add-dialog"
        aria-describedby={errorReason !== null ? 'api-key-add-error' : undefined}
        hideCloseButton
      >
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{ptBR.apiKeys.addDialogTitle}</DialogTitle>
            <DialogDescription>{ptBR.apiKeys.addDialogHint}</DialogDescription>
          </DialogHeader>

          <div className="mt-4">
            <Field htmlFor="api-key-add-provider" label={ptBR.apiKeys.addDialogProviderLabel}>
              <Select
                id="api-key-add-provider"
                data-testid="api-key-add-provider"
                value={provider}
                disabled={submitting}
                onChange={(e) => setProvider(e.target.value as ApiKeysProvider)}
              >
                {API_KEY_PROVIDERS.map((opcao) => (
                  <option key={opcao} value={opcao}>
                    {ptBR.apiKeys.addDialogProviderNames[PROVIDER_PAPEL[opcao]]}
                  </option>
                ))}
              </Select>
            </Field>
            <p className="mt-1 text-caption text-fg-muted">{ptBR.apiKeys.addDialogProviderHint}</p>
          </div>

          <label className="mt-4 flex flex-col gap-1.5">
            <span className="text-body-sm text-fg">{ptBR.apiKeys.addDialogValueLabel}</span>
            <Input
              type="password"
              required
              autoComplete="off"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={ptBR.apiKeys.addDialogValuePlaceholder}
              disabled={submitting}
              data-testid="api-key-add-value-input"
            />
          </label>

          {errorReason !== null ? (
            <p
              id="api-key-add-error"
              role="alert"
              data-testid="api-key-add-error"
              className="mt-4 border border-danger bg-danger-50 px-3 py-2 text-body-sm text-danger-700"
            >
              {mapApiKeysErrorReason(errorReason)}
            </p>
          ) : null}

          <p className="mt-3 border border-border bg-surface-2 px-3 py-2 text-caption text-fg-muted">
            {ptBR.apiKeys.revealOnceHint}
          </p>

          <DialogFooter>
            <Button
              variant="secondary"
              onClick={onCancel}
              disabled={submitting}
              data-testid="api-key-add-cancel"
            >
              {ptBR.apiKeys.addDialogCancel}
            </Button>
            <Button
              type="submit"
              disabled={submitting || value.length === 0}
              data-testid="api-key-add-confirm"
            >
              {submitting ? ptBR.apiKeys.saving : ptBR.apiKeys.addDialogConfirm}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
